import React, { useCallback, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { omit } from 'lodash'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import ReactPlaceholder from 'react-placeholder'
import { Wrapper, Article, ImageWrapper } from 'components/AdListItem/styled'
import styled from 'styled-components'
import { mediaQueries } from '@blazar-plugins/device'
import featuresPlugin from '@blazar-plugins/features'
import AdListItem from 'components/AdListItem'
import { ListTextPlaceholder, PlaceholderWrapper } from 'utils/LoadingAnimationStyles'
import { buildListUrl } from 'utils/url'
import { RectShape } from 'react-placeholder/lib/placeholders'
import { Package, PackageSmall } from 'components/OnboardingBanner/package.svg.js'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from '@blocket/tracking'
import { TextSubHeading, TextCallout2, Button } from 'blocket-ui'

const imageBorderRadius = { borderRadius: 2 }

export const ListPlaceholder = React.memo(({ device }) => (
  <div className="show-loading-animation">
    {Array.from(new Array(3)).map((_val, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={i}>
        <Wrapper>
          <Article>
            <ImageWrapper>
              <RectShape color="#f9f7f6" style={imageBorderRadius} />
            </ImageWrapper>
            <ListTextPlaceholder
              device={device}
              color="#f9f7f6"
              marginTop={device.is('medium') ? '75px' : '30px'}
            />
          </Article>
        </Wrapper>
      </React.Fragment>
    ))}
  </div>
))

ListPlaceholder.propTypes = {
  device: PropTypes.object.isRequired,
}

const SearchResultsWrapper = styled.div`
  padding-top: 0;
  border-bottom: 1px solid #bab9b8;
  .blocket-placement {
    border-bottom: 0.5px solid #e1e1e1;
  }
  @media ${mediaQueries.large} {
    border-bottom: 0;
  }
`

const ShippingTextWrapper = styled.div`
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.25);
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  overflow: hidden;
  margin-top: 24px;
  @media ${mediaQueries.medium} {
    max-height: 110px;
    margin-bottom: 4px;
  }
`

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  padding: 15px 25px 25px 25px;
  border-bottom: 1px solid #696766;
  @media ${mediaQueries.medium} {
    padding: 0;
    border: 0;
  }
  button {
    width: auto;
    height: 48px;
    margin-top: 10px;
    margin-bottom: 10px;
    @media ${mediaQueries.medium} {
      width: 50%;
    }
  }
`

const mapStateToProps = (state) => ({
  shippingAds: state.classified.shippingAds,
  totalShippingAds: state.classified.totalShippingAds,
  viewMode: state.searchParams.viewMode,
  isPremiumTooltipEnabled: featuresPlugin.selectors.isEnabled(state, 'premium-filter-tooltip'),
  parametersByQueryKey: state.searchParams.byQueryKey,
  isExtendedShippingEnabled: featuresPlugin.selectors.isEnabled(state, 'extended-shipping'),
})

export const ShippingAds = ({
  shippingAds,
  location,
  device,
  loading,
  isPremiumTooltipEnabled,
  history,
  parametersByQueryKey,
  isExtendedShippingEnabled,
}) => {
  const queryParams = queryString.parse(location.search)
  let newQuery = { ...queryParams, page: '1', sh: '1' }
  newQuery = omit(newQuery, 'r', 'm', 'sa')

  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: '100px 0px' })

  useEffect(() => {
    if (inView && shippingAds.ads.length > 0) {
      if (shippingAds.ads.length > 0) {
        const viewImpressionData = {
          eventName: 'Recommendation widget viewable impression',
          eventType: 'Engagement',
          object: {
            type: 'RecommendationList',
            numItems: shippingAds.ads.length,
            items: shippingAds.ads.map((item, index) => ({
              id: item.adId,
              rank: index,
              type: 'RecommendationItem',
            })),
          },
          recommendation: {
            recommendationType: 'extended-shipping-items',
            id: `${location.pathname}${location.search}`,
            source: 'ad-list',
          },
        }
        trackEvent(viewImpressionData)
      }
    }
  }, [inView, location.pathname, location.search, shippingAds.ads])

  const onShippingAdClick = useCallback(
    (ad, adListIndex) => {
      trackEvent({
        eventName: 'Recommendation widget clicked',
        eventType: 'Click',
        eventCategory: 'engagement',
        eventLabel: 'search for shipping ads',
        eventAction: 'clicked shipping ad',
        object: {
          name: 'Shipping ad clicked',
          type: 'RecommendationItem',
          id: ad.adId,
          rank: adListIndex,
        },
        recommendation: {
          recommendationType: 'extended-shipping-items',
          id: `${location.pathname}${location.search}`,
          source: 'ad-list',
          queryParams,
        },
      })
    },
    [location.pathname, location.search, queryParams],
  )

  const onShippingAdsButtonClick = useCallback(() => {
    trackEvent({
      eventType: 'Engagement',
      eventCategory: 'showMoreShippingAdsButton',
      eventLabel: 'Visa alla med frakt',
      eventAction: 'clicked button to show all shipping ads',
    })

    history.push(buildListUrl(newQuery, parametersByQueryKey))
  }, [history, newQuery, parametersByQueryKey])

  const shippingAdsList = shippingAds.ads.filter(Boolean).map((ad, index) => {
    const adListItem = (
      <AdListItem
        key={ad.adId}
        ad={ad}
        index={index}
        isPremiumTooltipEnabled={isPremiumTooltipEnabled}
        onClick={() => onShippingAdClick(ad, index)}
      />
    )

    return adListItem
  })

  if (!isExtendedShippingEnabled || !shippingAds.ads.length) {
    return null
  }

  return (
    <>
      <PlaceholderWrapper>
        <ReactPlaceholder ready={!loading} customPlaceholder={<ListPlaceholder device={device} />}>
          {shippingAdsList.length > 0 && (
            <>
              <ShippingTextWrapper ref={ref}>
                <div>
                  <TextSubHeading>Köp från hela Sverige!</TextSubHeading>
                  <TextCallout2>Annonser som erbjuder frakt och trygg betalning.</TextCallout2>
                </div>
                <div>
                  {device.is('large') ? (
                    <Package cloudColor="#f0f5fb" />
                  ) : (
                    <PackageSmall width={54} height={57} cloudColor="#f0f5fb" />
                  )}
                </div>
              </ShippingTextWrapper>
              <SearchResultsWrapper>{shippingAdsList}</SearchResultsWrapper>{' '}
              {shippingAds.totalShippingAdsCount > 3 && (
                <StyledButton type="secondary" onClick={onShippingAdsButtonClick}>
                  Visa alla med frakt ({shippingAds.totalShippingAdsCount})
                </StyledButton>
              )}
            </>
          )}
        </ReactPlaceholder>
      </PlaceholderWrapper>
    </>
  )
}

ShippingAds.propTypes = {
  shippingAds: PropTypes.object.isRequired,
  location: PropTypes.object,
  device: PropTypes.shape({ is: PropTypes.func.isRequired }).isRequired,
  loading: PropTypes.bool,
  isPremiumTooltipEnabled: PropTypes.bool,
  history: PropTypes.object.isRequired,
  parametersByQueryKey: PropTypes.object,
  isExtendedShippingEnabled: PropTypes.bool,
}

export default withRouter(
  connect(
    mapStateToProps,
    null,
  )(ShippingAds),
)
